/**
@jsxRuntime
classic */
/** @jsx jsx */
import {jsx} from '@emotion/core'
import {Routes, Route, useLocation, Navigate} from 'react-router-dom'
import * as Sentry from '@sentry/react'
import {useAuth0} from '@auth0/auth0-react'

import {HeaderTitle} from './components/header-title'
import NotFoundPage from './components/not-found-page'
import {resolveKeapPartnerId} from './utils/resolveKeapPartnerId'
import {
  ErrorContainer,
  TextContainer,
  ImageContainer,
  ItemContainer,
  ScreenContainer,
} from './components/ui/error-fallback'
import {thryvGuyConfused} from './assets'
import {ParagraphText, Button} from '@thryvlabs/maverick'
import {LandingPageKP} from './components/keap-partner/kp-landing-page/kp-landing-page'
// import {KeapPartnerSharedLinkLayoutComponent} from './components/keap-partner/kp-shared-link/kp-sharedLink-layout'
import {KeapPartnerAccountDetails} from './components/keap-partner-account-information/account-information'
import {KP11LinkLayoutComponent} from './components/keap-partner/kp-11-link/kp-11Link-layout'
import {KPManyLinkPage} from './components/keap-partner/kp-11-link/kp-many-link/KPManyLinkPage'
import {KPCountrySelectionComponent} from './components/keap-partner/kp-country-selection/kp-country-selection'
import ControlCenter from './components/control-center-my-accounts/control-center'
import {Accounts} from './accountManagement/Accounts'
import {MyAccounts} from './accountManagement/features/AccountsTabs/MyAccounts'
import {AccountSetupPage} from './accountManagement/features/AccountSetup'
import {UpgradeOrder} from './screens/upgrade-order'
import {getUserRoles} from './utils/get-user-roles'
import {useSelector} from 'react-redux'
import {selectedLinkType} from './app-store/kpGlobalStateSlice'

const ERROR_STYLES = {
  title: {
    color: '#ff5101',
    fontWeight: 'bold',
    fontSize: '2rem',
    marginBottom: '20px',
  },
  subtitle: {
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  button: {
    marginTop: '50px',
  },
}

function FullPageErrorFallback({error}) {
  const handleClick = () => window.location.reload()

  return (
    <ScreenContainer>
      <ErrorContainer>
        <ItemContainer>
          <TextContainer>
            <ParagraphText variant="reg" style={ERROR_STYLES.title}>
              Uh Oh!
            </ParagraphText>
            <ParagraphText
              variant="reg"
              className="m-0"
              style={ERROR_STYLES.subtitle}
            >
              There&apos;s a problem. Try refreshing the app.
            </ParagraphText>
          </TextContainer>
          <Button
            onClick={handleClick}
            variant="primary"
            style={ERROR_STYLES.button}
          >
            Refresh Now
          </Button>
        </ItemContainer>
        <ImageContainer>
          <img src={thryvGuyConfused} alt="confused Thryv Guy" />
        </ImageContainer>
      </ErrorContainer>
    </ScreenContainer>
  )
}

function AuthenticatedKeapPartnerApp({userData}) {
  //const location = useLocation()
  // state.user.keapPartnerId is only populated from a URL ?pac= param / sessionStorage,
  // which are absent in the in-pane KP flow — so it's null here. Fall back to the
  // canonical value in user_metadata so partner-scoped views (e.g. My Accounts) hit the
  // partner-accounts endpoint instead of falling through to rep-accounts (403 for KP).
  const storedKeapPartnerId = useSelector(state => state.user?.keapPartnerId)
  const keapPartnerId = resolveKeapPartnerId({
    storedKeapPartnerId,
    user: userData,
  })
  return (
    <Sentry.ErrorBoundary fallback={FullPageErrorFallback}>
      <Routes>
        <Route
          path="/"
          element={
            <HeaderTitle title="Keap Partner Home">
              <LandingPageKP userData={userData} />
            </HeaderTitle>
          }
        />
        <Route
          path="/keap-partner-country-selection"
          element={
            <HeaderTitle title="Country Selection">
              <KPCountrySelectionComponent
                keapPartnerId={keapPartnerId}
                userData={userData}
              />
            </HeaderTitle>
          }
        />
        <Route
          path="/keap-partner-catalog"
          element={<KeapPartnerRouteWrapper userData={userData} />}
        />
        <Route
          path="/keap-partner-account"
          element={
            <HeaderTitle title="Account Information">
              <KeapPartnerAccountDetails userData={userData} />
            </HeaderTitle>
          }
        />
        <Route
          path="/control-center"
          element={
            <HeaderTitle title="Control Center">
              <ControlCenter
                userData={userData}
                keapPartnerId={keapPartnerId}
                hideAside
              />
            </HeaderTitle>
          }
        />
        {/* Account Management (FULSAS-10234) — reuses the Core OOE account
            management UI inside the Keap Partner shell. Keap partners get the
            My Accounts view only (All Accounts is hidden) plus New Account. */}
        <Route path="/accounts" element={<Accounts />}>
          <Route
            index
            element={<Navigate to="/accounts/my-accounts" replace />}
          />
          <Route
            path="my-accounts"
            element={
              <HeaderTitle title="My Accounts">
                <MyAccounts isKeapPartner keapPartnerId={keapPartnerId} />
              </HeaderTitle>
            }
          />
          <Route
            path="account-setup"
            element={
              <HeaderTitle title="New Account">
                <AccountSetupPage keapPartnerId={keapPartnerId} />
              </HeaderTitle>
            }
          />
        </Route>
        {/* My Accounts "Create Purchase Link" for legacy (non-v5) accounts
            navigates here; without this route the KP shell 404s. */}
        <Route
          path="/order/upgrade"
          element={
            <HeaderTitle title="Upgrade Order">
              <UpgradeOrder />
            </HeaderTitle>
          }
        />
        <Route
          path="*"
          element={
            <HeaderTitle title="404 - Not Found">
              <NotFoundPage />
            </HeaderTitle>
          }
        />
      </Routes>
    </Sentry.ErrorBoundary>
  )
}

function KeapPartnerRouteWrapper({userData}) {
  const location = useLocation()
  const {user} = useAuth0()
  const userRoles = getUserRoles(user)
  const keapPartnerId = useSelector(state => state.user?.keapPartnerId)
  // Use userData.user_roles if available (enriched from backend), otherwise use userRoles from Auth0
  const effectiveUserRoles = userData?.user_roles || userRoles
  const isInfluencerOps = effectiveUserRoles.includes('InfluencerOps')
  const isKeapPartner = effectiveUserRoles.includes('KeapPartner')

  let selectedCountry = location.state?.selectedCountry || 'US'
  let navLinkType = location.state?.linkType
  let linkType = useSelector(selectedLinkType) || navLinkType
  // If keapPartnerId exists but linkType doesn't, set default linkType
  if (keapPartnerId && !linkType) {
    linkType = '1:many'
  }

  if (!linkType) {
    // Both InfluencerOps and KeapPartner users should see country selection
    if (isInfluencerOps || isKeapPartner) {
      return <Navigate to="/keap-partner-country-selection" replace />
    } else {
      return (
        <Navigate
          to={location.pathname}
          replace
          state={{linkType: '1:many', selectedCountry, keapPartnerId}}
        />
      )
    }
  }

  // 1:Many renders on the shared Core Catalog (CatalogGrid / OrderSummary),
  // like the 1:1 Purchase Link. `?legacy=1` falls back to the previous page.
  const useLegacyManyPage =
    new URLSearchParams(location.search).get('legacy') === '1'
  if (linkType === '1:many' && !useLegacyManyPage) {
    return (
      <HeaderTitle title="Thryv Catalog">
        <KPManyLinkPage
          keapPartnerId={keapPartnerId}
          userData={userData}
          key={location.key}
        />
      </HeaderTitle>
    )
  }

  return (
    <HeaderTitle title="Thryv Catalog">
      {linkType && linkType.length > 0 && (
        <KP11LinkLayoutComponent
          linkType={linkType}
          keapPartnerId={keapPartnerId}
          selectedCountry={selectedCountry}
        />
      )}
      {/* {linkType === '1:1' && (
        <KP11LinkLayoutComponent
          linkType={linkType}
          keapPartnerId={keapPartnerId}
          selectedCountry={selectedCountry}
        />
      )}
      {linkType === '1:many' && (
        <KeapPartnerSharedLinkLayoutComponent
          linkType={linkType}
          keapPartnerId={keapPartnerId}
          selectedCountry={selectedCountry}
        />
        
      )} */}
    </HeaderTitle>
  )
}

export default AuthenticatedKeapPartnerApp
